<!-- Work dates of the video dialog: two rows, the work start (workStart) and the end of work = publication
     (publishDate / publishTime), each with its day written in full ("Tuesday 3 November", DayField.vue: a calendar of
     one month) and its time (TimeField.vue: 15-minute slots). The end's slots come after the start on the same day and
     show the duration; the end can have no time (the end of that day). A start chosen by its day gets 9:00; when a
     change would cross the dates, the other one moves by as much (same length of work). × clears that row. Logic and
     stored formats in src/features/workSlot.ts. -->
<script setup lang="ts">
import { computed } from 'vue'
import DayField from './DayField.vue'
import TimeField from './TimeField.vue'
import { useI18n } from '../../composables/useI18n'
import { SLOTS, clearEnd, clearStart, endDay, endSlots, setEndDay, setEndTime, setStartDay, setStartTime, startDay, startTime, type WorkSlot } from '../../features/workSlot'

const props = defineProps<{ endLabel: string; startLabel: string }>()
const workStart = defineModel<string>('workStart', { required: true })
const publishDate = defineModel<string>('publishDate', { required: true })
const publishTime = defineModel<string>('publishTime', { required: true })
const { t } = useI18n()

const cur = computed<WorkSlot>(() => ({ workStart: workStart.value || '', publishDate: publishDate.value || '', publishTime: publishTime.value || '' }))
function apply(v: WorkSlot): void {
  if (v.workStart !== workStart.value) workStart.value = v.workStart
  if (v.publishDate !== publishDate.value) publishDate.value = v.publishDate
  if (v.publishTime !== publishTime.value) publishTime.value = v.publishTime
}
const sDay = computed(() => startDay(cur.value)), sTime = computed(() => startTime(cur.value))
const eDay = computed(() => endDay(cur.value))
const startOptions = SLOTS.map(time => ({ time, dur: '' }))
const endOptions = computed(() => endSlots(cur.value))
/** The end's list opens one hour after the start when there is no end time yet */
const endNear = computed(() => (sTime.value && (!eDay.value || eDay.value === sDay.value) ? endOptions.value[3]?.time || '' : ''))

const ROW = 'flex items-center gap-2'
const CLEAR = 'w-8 h-8 flex-shrink-0 rounded-full text-gray-500 hover:text-brand hover:bg-brand/10'
</script>

<template>
  <div class="space-y-3">
    <!-- Work start -->
    <div>
      <div class="text-[11px] font-bold text-gray-400 mb-1">{{ props.startLabel }}</div>
      <div :class="ROW">
        <DayField id="workStartDay" :label="props.startLabel" :value="sDay" :other="eDay" :placeholder="t('Choose the day')" @pick="d => apply(setStartDay(cur, d))" />
        <TimeField id="workStartTime" :label="props.startLabel" :value="sTime" :options="startOptions" :placeholder="t('Start')"
          :disabled="!sDay && !eDay" :disabled-title="t('Choose the day first')" @pick="h => apply(setStartTime(cur, h))" />
        <button v-if="sDay" type="button" :title="t('Clear the start')" :aria-label="t('Clear the start')" :class="CLEAR" @click="apply(clearStart(cur))"><i class="fas fa-xmark"></i></button>
        <span v-else class="w-8 flex-shrink-0" aria-hidden="true"></span>
      </div>
    </div>
    <!-- End of work = publication -->
    <div>
      <div class="text-[11px] font-bold text-gray-400 mb-1">{{ t('End of work · publishing') }}</div>
      <div :class="ROW">
        <DayField id="workDay" :label="props.endLabel" :value="eDay" :other="sDay" :placeholder="t('Choose the day')" @pick="d => apply(setEndDay(cur, d))" />
        <TimeField id="workEndTime" :label="props.endLabel" :value="publishTime" :options="endOptions" :placeholder="t('End')" none :near="endNear"
          :disabled="!eDay && !sDay" :disabled-title="t('Choose the day first')" @pick="h => apply(setEndTime(cur, h))" />
        <button v-if="eDay || publishTime" type="button" :title="t('Clear the end of work')" :aria-label="t('Clear the end of work')" :class="CLEAR" @click="apply(clearEnd(cur))"><i class="fas fa-xmark"></i></button>
        <span v-else class="w-8 flex-shrink-0" aria-hidden="true"></span>
      </div>
    </div>
  </div>
</template>
